// pages/admin/gifts/index.jsx
import { useNavigate, useSearchParams } from "react-router-dom";
import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { useQuery } from "@tanstack/react-query";
import { lazily } from "react-lazily";
import { giftsService } from "@/api/awards";
import { FiPlus, FiGift, FiSearch } from "react-icons/fi";
import { useTranslation } from "@/hooks/useTranslation";

const { AdminGiftsGrid } = lazily(() =>
    import("@/pages/admin/gifts/AdminGiftsList")
);
const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

const AdminGifts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const currentPage = parseInt(params.get("page")) || 0;
  const typeFilter = params.get("type") || "ALL";
  const searchFilter = params.get("search") || "";

  // State for delete confirmation
  const [giftToDelete, setGiftToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [giftToToggle, setGiftToToggle] = useState(null);

  const fetchData = useCallback(
      async () => {
        const params = {
          page: currentPage,
          size: 10,
        };

        if (typeFilter !== "ALL") {
          params.type = typeFilter;
        }

        if (searchFilter) {
          // Assuming your API supports search
          // You might need to adjust this based on your API
          params.search = searchFilter;
        }

        return await giftsService.getGifts(
            params.page,
            params.size,
            params.type,
            params.search
        );
      },
      [currentPage, typeFilter, searchFilter]
  );

  const {
    isPending: loading,
    data: giftsData,
    error,
    refetch,
  } = useQuery({
    queryKey: ["gifts", currentPage, typeFilter, searchFilter],
    queryFn: fetchData,
    retry: false,
  });

  const [giftItems, setGiftItems] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalItems: 0,
    totalPages: 1
  });

  useEffect(() => {
    if (loading || error || !giftsData) return;

    // Handle API response structure
    if (giftsData.result && Array.isArray(giftsData.result)) {
      setGiftItems(giftsData.result);
    } else if (Array.isArray(giftsData)) {
      setGiftItems(giftsData);
    } else {
      setGiftItems([]);
    }

    // Set pagination from API response
    setPagination({
      currentPage: giftsData.currentPage || currentPage,
      totalItems: giftsData.totalItems || 0,
      totalPages: giftsData.totalPages || 1
    });
  }, [giftsData, loading, error, currentPage]);

  const updateGift = useCallback(
      (id, props) => {
        const index = giftItems.findIndex((e) => e.id === id);
        if (index === -1) return;
        let clone = { ...giftItems[index], ...props };
        setGiftItems([
          ...giftItems.slice(0, index),
          clone,
          ...giftItems.slice(index + 1),
        ]);
      },
      [giftItems]
  );

  const handleDeleteClick = useCallback((id) => {
    setGiftToDelete(id);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!giftToDelete) return;

    setIsDeleting(true);
    try {
      await giftsService.deleteGift(giftToDelete);
      setGiftItems((prev) => prev.filter((gift) => gift.id !== giftToDelete));
      setPagination(prev => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1)
      }));
      console.log("Gift deleted successfully!");
    } catch (error) {
      console.error("Failed to delete gift:", error);
    } finally {
      setIsDeleting(false);
      setGiftToDelete(null);
    }
  }, [giftToDelete]);

  const cancelDelete = useCallback(() => {
    setGiftToDelete(null);
  }, []);

  const handleToggleDisplay = useCallback(async (gift) => {
    setIsToggling(true);
    setGiftToToggle(gift.id);
    try {
      const updatedGift = {
        ...gift,
        display: !gift.display
      };

      await giftsService.updateGift(gift.id, updatedGift);
      updateGift(gift.id, { display: !gift.display });
      console.log("Gift updated successfully!");
    } catch (error) {
      console.error("Failed to update gift:", error);
    } finally {
      setIsToggling(false);
      setGiftToToggle(null);
    }
  }, [updateGift]);

  const loadingItem = useMemo(
      () => (
          <div className="size-full z-20 absolute flex items-center justify-center grow">
            <TaleLoadingFallback />
          </div>
      ),
      []
  );

  const handleFilterChange = (type, value) => {
    const params = new URLSearchParams(window.location.search);
    params.set(type, value);
    params.set("page", "0");
    navigate(`/admin/gifts?${params.toString()}`);
  };

  const handleSearch = (value) => {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    params.set("page", "0");
    navigate(`/admin/gifts?${params.toString()}`);
  };

  const handleClearSearch = () => {
    handleSearch("");
  };

  const giftTypes = [
    { value: "ALL", label: t("giftManagement.gifts.typeAll") },
    { value: "SHOPPING_VOUCHER", label: t("giftManagement.gifts.typeCoupons") },
    { value: "PHONE_RECHARGE_CARD", label: t("giftManagement.gifts.typeRecharges") },
  ];

  // Calculate stats from gift items
  const calculateStats = () => {
    return {
      total: giftItems.length,
      visible: giftItems.filter(g => g.display === true).length,
      hidden: giftItems.filter(g => g.display === false).length,
      coupons: giftItems.filter(g => g.type === "SHOPPING_VOUCHER").length,
      recharges: giftItems.filter(g => g.type === "PHONE_RECHARGE_CARD").length
    };
  };

  const itemStats = calculateStats();

  return (
      <>
        {/* Delete Confirmation Modal */}
        {giftToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t("giftManagement.gifts.confirmDeleteTitle")}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t("giftManagement.gifts.confirmDeleteMessage")}
                  </p>
                  <div className="flex justify-end gap-3">
                    <TaleButton
                        variant="outlined"
                        onClick={cancelDelete}
                        disabled={isDeleting}
                    >
                      {t("common.cancel")}
                    </TaleButton>
                    <TaleButton
                        onClick={confirmDelete}
                        loading={isDeleting}
                        className="bg-red-600 hover:bg-red-700 border-red-600"
                    >
                      {t("common.delete")}
                    </TaleButton>
                  </div>
                </div>
              </div>
            </div>
        )}

        <TaleDashbaordMainCard className={"flex flex-col gap-6"}>
          <div className="gifts-header">
            <div className="w-full flex justify-between items-center">
              <div className="flex flex-col">
                <h2 className="!text-2xl font-bold !m-0">{t("giftManagement.gifts.listTitle")}</h2>
                <p className="text-gray-600">
                  {t("giftManagement.gifts.listSubtitle")}
                </p>
              </div>
              <div className="flex gap-3">
                <TaleButton
                    role="normal"
                    className="!px-4"
                    onClick={() => navigate("/admin/gifts/new")}
                >
                  <FiPlus className="mr-2 rtl:mr-0 rtl:ml-2" />
                  {t("giftManagement.gifts.addNewGift")}
                </TaleButton>
                <TaleButton
                    role="normal"
                    variant="outlined"
                    onClick={() => refetch()}
                >
                  <i className="fas fa-sync-alt mr-2 rtl:mr-0 rtl:ml-2"></i>
                  {t("giftManagement.gifts.refresh")}
                </TaleButton>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex gap-2">
              <select
                  className="px-4 py-2 border rounded-lg"
                  value={typeFilter}
                  onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                {giftTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                ))}
              </select>
            </div>

            <div className="ml-auto rtl:ml-0 rtl:mr-auto w-full md:w-auto">
              <div className="relative">
                <input
                    type="text"
                    placeholder={t("giftManagement.gifts.searchPlaceholder")}
                    className="px-4 py-2 border rounded-lg w-full md:w-64 pl-10 rtl:pl-4 rtl:pr-10"
                    value={searchFilter}
                    onChange={(e) => handleSearch(e.target.value)}
                />
                <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchFilter && (
                    <button
                        className="absolute right-2 rtl:right-auto rtl:left-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={handleClearSearch}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          {!loading && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white border p-4 rounded-xl">
                  <div className="text-sm text-blue-600">{t("giftManagement.gifts.totalGifts")}</div>
                  <div className="text-2xl font-bold">{pagination.totalItems}</div>
                </div>
                <div className="bg-white border p-4 rounded-xl">
                  <div className="text-sm text-green-600">{t("giftManagement.gifts.visible")}</div>
                  <div className="text-2xl font-bold">
                    {itemStats.visible}
                  </div>
                </div>
                <div className="bg-white border p-4 rounded-xl">
                  <div className="text-sm text-yellow-600">{t("giftManagement.gifts.hidden")}</div>
                  <div className="text-2xl font-bold">
                    {itemStats.hidden}
                  </div>
                </div>
                <div className="bg-white border p-4 rounded-xl">
                  <div className="text-sm text-purple-600">{t("giftManagement.gifts.typeCoupons")}</div>
                  <div className="text-2xl font-bold">
                    {itemStats.coupons}
                  </div>
                </div>
                <div className="bg-white border p-4 rounded-xl">
                  <div className="text-sm text-pink-600">{t("giftManagement.gifts.typeRecharges")}</div>
                  <div className="text-2xl font-bold">
                    {itemStats.recharges}
                  </div>
                </div>
              </div>
          )}

          {/* Gifts List */}
          <div
              className={`flex flex-col relative grow size-full ${
                  !giftItems.length && !loading ? 'items-center justify-center gap-4' : "gap-4"
              }`}
          >
            {loading && loadingItem}
            {error && !loading ? (
                <div className="size-full z-20 absolute flex items-center justify-center grow">
                  <InternalServerError />
                </div>
            ) : null}
            <Suspense fallback={loadingItem}>
              {loading || error ? null : !giftItems.length ? (
                  <>
                    <FiGift className="w-16 h-16 text-gray-400" />
                    <span className="text-[#333333] text-lg">
                {t("giftManagement.gifts.noGiftsFound")}
            </span>
                    {searchFilter && (
                        <button
                            onClick={handleClearSearch}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          {t("giftManagement.gifts.clearSearch")}
                        </button>
                    )}
                  </>
              ) : (
                  // Choose either the table or grid layout:
                  <AdminGiftsGrid
                      gifts={giftItems}
                      updateGift={updateGift}
                      onDeleteClick={handleDeleteClick}
                      onToggleDisplay={handleToggleDisplay}
                      isToggling={isToggling}
                      giftToToggle={giftToToggle}
                  />
              )}
            </Suspense>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2"
                    disabled={currentPage <= 0}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set("page", Math.max(0, currentPage - 1));
                      navigate(`/admin/gifts?${params.toString()}`);
                    }}
                >
                  <i className="fas fa-chevron-left rtl:-scale-x-100"></i>
                  {t("giftManagement.gifts.previous")}
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;

                    if (pagination.totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 2) {
                      pageNum = i;
                    } else if (currentPage > pagination.totalPages - 3) {
                      pageNum = pagination.totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                        <button
                            key={pageNum}
                            className={`w-10 h-10 flex items-center justify-center border rounded-lg ${
                                currentPage === pageNum
                                    ? "bg-blue-500 text-white border-blue-500"
                                    : "hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              const params = new URLSearchParams(window.location.search);
                              params.set("page", pageNum);
                              navigate(`/admin/gifts?${params.toString()}`);
                            }}
                        >
                          {pageNum + 1}
                        </button>
                    );
                  })}

                  {pagination.totalPages > 5 && currentPage < pagination.totalPages - 3 && (
                      <>
                        <span className="mx-1">...</span>
                        <button
                            className="w-10 h-10 flex items-center justify-center border rounded-lg hover:bg-gray-50"
                            onClick={() => {
                              const params = new URLSearchParams(window.location.search);
                              params.set("page", pagination.totalPages - 1);
                              navigate(`/admin/gifts?${params.toString()}`);
                            }}
                        >
                          {pagination.totalPages}
                        </button>
                      </>
                  )}
                </div>

                <button
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-2"
                    disabled={currentPage >= pagination.totalPages - 1}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set("page", currentPage + 1);
                      navigate(`/admin/gifts?${params.toString()}`);
                    }}
                >
                  {t("giftManagement.gifts.next")}
                  <i className="fas fa-chevron-right rtl:-scale-x-100"></i>
                </button>

                <div className="ml-4 rtl:ml-0 rtl:mr-4 text-sm text-gray-600 hidden md:block">
                  {t("giftManagement.gifts.totalGiftsCount", { count: pagination.totalItems })}
                </div>
              </div>
          )}
        </TaleDashbaordMainCard>
      </>
  );
};

export default AdminGifts;
